import { LitElement, css, unsafeCSS, html, svg, property } from "lit-element";
import { v4 as uuid } from 'uuid';
import { StratusStatsCardComponentTemplateModel } from "../../models/components/StratusStatsCardComponentTemplateModel";

import playerBreakdownStyles from './team-breakdown-table.scss';
import bootstrap from './bootstrap-tables.scss';

import { StratusStatsCardTableHeadingTemplateModel } from "../../models/components/StratusStatsCardTableHeadingTemplateModel";
import { StratusStatsCardTeamTemplateModel } from "../../models/components/StratusStatsCardTeamTemplateModel";

export class TeamBreakdownTable extends LitElement {
    private _cardComponentTemplate: StratusStatsCardComponentTemplateModel;
    private _headerColumns: StratusStatsCardTableHeadingTemplateModel[];
    private _id: any;
    private _onClick: Function;
    private _playlistClickThrough: boolean;

    constructor(data: StratusStatsCardComponentTemplateModel, headerColumns: StratusStatsCardTableHeadingTemplateModel[], playlistClickThrough: boolean, onClick: Function) {
        super();

        this._cardComponentTemplate = data;
        this._headerColumns = headerColumns;
        this._onClick = onClick;
        this._playlistClickThrough = playlistClickThrough;

        this._id = `table-${uuid()}`;
    }

    static get styles() {
        return [
            css`${unsafeCSS(playerBreakdownStyles)}`,
            css`${unsafeCSS(bootstrap)}`
        ]
    }

    buildHeaders(showFullNames) {
        let htmlContent;

        this._headerColumns.sort(this.sortRowColumns);

        if (showFullNames == true) {
            htmlContent = html`${this._headerColumns.map((tableHeading, index) => html`<th>${tableHeading.fullName}</th>`)}`;
        }
        else {
            htmlContent = html`${this._headerColumns.map((tableHeading, index) => html`<th data-fullname="${tableHeading.fullName}" @touchstart=${this.headerClick} @mouseover="${this.headerClick}" @mouseout="${this.clearHeader}">${tableHeading.abbreviation}</th>`)}`;
        }

        return htmlContent;
    }

    clearHeader(event) {
        event.target.classList.remove('active');
    }

    headerClick(event) {
        let activeHeader = this.shadowRoot.querySelector('th.active');

        if (activeHeader)
            activeHeader.classList.remove('active');

        if (event.target != activeHeader)
            event.target.classList.add('active');
    }

    sortRowColumns(a, b) {
        if (a.order > b.order)
            return 1;
        else if (a.order < b.order)
            return -1;
        else
            return 0;
    }

    buildBody(teams: StratusStatsCardTeamTemplateModel[]) {
        let htmlContent;

        teams.sort((a, b) => {
            if (a.name > b.name)
                return 1;
            else if (a.name < b.name)
                return -1;
            else
                return 0;
        });

        if (teams[0].group) {
            teams.sort((a, b) => {
                if (a.group > b.group)
                    return 1;
                else if (a.group < b.group)
                    return -1;
                else
                    return 0;
            });
        }

        for (let [i, team] of Object.entries(teams)) {

            htmlContent = html`${htmlContent}
            <tr>                
                <td class="team-details"> 
                    <img src="https://mobiithumbnails.azureedge.net/thumbnails/live/stratus/${team.id}/small.png" />                       
                    ${team.name}
                </td>
                ${team.group ? html`<td style="white-space: nowrap;">${team.group}</td>` : ''}

                ${team.columnValues.map((columnValue, index) => {
                let header: StratusStatsCardTableHeadingTemplateModel = this._headerColumns[index];

                let conditionalFormat: string = "";
                if (columnValue.isMax)
                    conditionalFormat = "max-value";
                else if (columnValue.isMin)
                    conditionalFormat = "min-value";
                else if (columnValue.isBelowThreshold)
                    conditionalFormat = "below-threshold-value";

                return html`<td class="${(this._playlistClickThrough ? "clickable" : "")} ${(conditionalFormat)}" 
                    @click="${async (event) => await this._onClick(event, header.metricId, team.id, header.fullName)}"
                    title="${header.fullName}">${columnValue.value}</td>`
            })}
            </tr>`;
        }

        return htmlContent;
    }

    exportToCSV(teams: StratusStatsCardTeamTemplateModel[]) {

        let hasGroups = false;

        this._headerColumns.sort(this.sortRowColumns);

        teams.sort((a, b) => {
            if (a.name > b.name)
                return 1;
            else if (a.name < b.name)
                return -1;
            else
                return 0;
        });

        if (teams[0].group) {

            hasGroups = true;

            teams.sort((a, b) => {
                if (a.group > b.group)
                    return 1;
                else if (a.group < b.group)
                    return -1;
                else
                    return 0;
            });
        }

        let csvContent = "data:text/csv;charset=utf-8,";

        // Build Headers
        csvContent += `Player Name,`;

        if (hasGroups)
            csvContent += `Group,`;

        for (let [i, tableHeading] of Object.entries(this._headerColumns)) {
            if (parseInt(i) != this._headerColumns.length - 1) {
                csvContent += `${tableHeading.fullName},`;
            }
            else {
                csvContent += `${tableHeading.fullName}`;
            }
        }

        csvContent += "\n";

        // Build Body of Teams
        for (let [i, team] of Object.entries(this._cardComponentTemplate.teams)) {

            csvContent += `${team.name},`;

            if (hasGroups)
                csvContent += `${team.group},`;

            for (let [i, columnValue] of Object.entries(team.columnValues)) {

                if (parseInt(i) != team.columnValues.length - 1) {
                    csvContent += `${columnValue.value},`;
                }
                else {
                    csvContent += `${columnValue.value}`;
                }

            };

            csvContent += "\n";
        }

        let fileName = `${this._cardComponentTemplate.seasonName ?? this._cardComponentTemplate.seriesName}`;
        let encodedUri = encodeURI(csvContent);
        let link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `${fileName} Team Report.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    render() {
        return [
            html`
            <table class="table" id=${this._id}>
                <thead>       
                    <tr>
                    ${this._cardComponentTemplate.showExportToCSV ? 
                        html`
                        <th colspan="2">
                            <button id="csv-export-btn" class="btn btn-primary" @click=${() => this.exportToCSV(this._cardComponentTemplate.teams)}>
                                <i class="fa fa-file-export"></i> Export to CSV
                            </button>
                        </th>` : ``}
                        <th colspan="${this._headerColumns.length}">                            
                            <h5>${this._cardComponentTemplate.seasonName ?? this._cardComponentTemplate.seriesName}</h5>
                        </th>
                    </tr>             
                    <tr>                        
                        <th class="team-name-column">TEAM</th>
                        ${(this._cardComponentTemplate.teams[0].group) ?
                    html`<th><span class="desktop">GROUP</span></th>` : ''}

                        ${this.buildHeaders(this._cardComponentTemplate.showFullMetricName)}
                    </tr>
                </thead>
                <tbody>
                    ${this.buildBody(this._cardComponentTemplate.teams)}
                </tbody>        
            </table>`
        ]
    }
}

window.customElements.define('team-breakdown-table', TeamBreakdownTable as any);